/**
* Responsibility:
* - Normalize locale-dependent numeric strings for consistent parsing.
*
* Touches:
* - Removes spaces and harmonizes `,`/`.` decimal-thousand separators.
* - Treats repeated 3-digit groups as thousands separators.
* - Uses the last separator as the decimal separator when both `,` and `.` are present.
*
* Guarantees:
* - Returns parser-friendly numeric string or empty string.
*/
export function normalizeNumericString(value) {
	const input = String(value || '').trim().replace(/\s+/g, '');
	if (input === '') {
		return '';
	}

	const hasComma = input.includes(',');
	const hasDot = input.includes('.');
	if (hasComma && hasDot) {
		const decimalSeparator = input.lastIndexOf(',') > input.lastIndexOf('.') ? ',' : '.';
		const thousandsSeparator = decimalSeparator === ',' ? '.' : ',';
		const withoutThousands = input.split(thousandsSeparator).join('');
		return decimalSeparator === ','
			? withoutThousands.replace(',', '.')
			: withoutThousands;
	}

	if (hasComma) {
		if (/^-?\d{1,3}(,\d{3})+$/.test(input)) {
			return input.replace(/,/g, '');
		}

		return input.replace(',', '.');
	}

	if (hasDot && /^-?\d{1,3}(\.\d{3})+$/.test(input)) {
		return input.replace(/\./g, '');
	}

	return input;
}

/**
* Responsibility:
* - Convert input to integer using normalized numeric parsing.
*
* Touches:
* - Uses `normalizeNumericString()` and `Number.parseInt()`.
*
* Guarantees:
* - Returns finite integer when parsing succeeds.
* - Returns `null` when input is blank, malformed, or not an integer.
*/
export function toInteger(value) {
	if (typeof value === 'number') {
		return Number.isInteger(value) ? value : null;
	}

	const normalized = normalizeNumericString(value);
	if (!/^-?\d+$/.test(normalized)) {
		return null;
	}

	const parsed = Number.parseInt(normalized, 10);
	return Number.isFinite(parsed) ? parsed : null;
}

/**
* Responsibility:
* - Convert input to floating-point number using normalized numeric parsing.
*
* Touches:
* - Uses `normalizeNumericString()` and `Number.parseFloat()`.
*
* Guarantees:
* - Returns finite number when parsing succeeds.
* - Returns `null` when input is blank or malformed.
*/
export function toNumber(value) {
	if (typeof value === 'number') {
		return Number.isFinite(value) ? value : null;
	}

	const normalized = normalizeNumericString(value);
	if (!/^-?(?:\d+\.?\d*|\.\d+)$/.test(normalized)) {
		return null;
	}

	const parsed = Number.parseFloat(normalized);
	return Number.isFinite(parsed) ? parsed : null;
}

/**
* Responsibility:
* - Convert mixed values to boolean based on common checkbox/radio conventions.
*
* Touches:
* - Handles booleans, numbers, arrays, and normalized string values.
*
* Guarantees:
* - Returns strict boolean for recognized checkbox/radio values.
* - Returns `null` for malformed non-boolean values.
*/
export function toBoolean(value) {
	if (typeof value === 'boolean') {
		return value;
	}

	if (typeof value === 'number') {
		if (value === 1) {
			return true;
		}

		if (value === 0) {
			return false;
		}

		return null;
	}

	if (Array.isArray(value)) {
		return value.length > 0 ? true : false;
	}

	const normalized = String(value || '').trim().toLowerCase();
	if (normalized === '') {
		return false;
	}

	if (['1', 'true', 'yes', 'on'].includes(normalized)) {
		return true;
	}

	if (['0', 'false', 'no', 'off'].includes(normalized)) {
		return false;
	}

	return null;
}

/**
* Responsibility:
* - Produce deterministic JSON-like serialization for payload signatures.
*
* Touches:
* - Recursively sorts object keys while preserving array order.
*
* Guarantees:
* - Equivalent structures serialize to identical strings.
*/
function stableSerialize(value) {
	if (Array.isArray(value)) {
		return `[${value.map((item) => stableSerialize(item)).join(',')}]`;
	}

	if (value && typeof value === 'object') {
		const objectValue = value;
		const keys = Object.keys(objectValue).sort();
		const entries = keys.map((key) => `${JSON.stringify(key)}:${stableSerialize(objectValue[key])}`);
		return `{${entries.join(',')}}`;
	}

	return JSON.stringify(value);
}

/**
* Responsibility:
* - Build a deterministic payload signature string for cache/invalidation logic.
*
* Touches:
* - Serializes payload object via `stableSerialize()`.
*
* Guarantees:
* - Returns empty string when payload is not an object.
*/
export function buildPayloadSignature(payload) {
	if (!payload || typeof payload !== 'object') {
		return '';
	}

	return stableSerialize(payload);
}
